import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useEffect } from "react";
import { parsePartialJson } from "@ui/components/spec-renderer/parse-partial-json";
import { useScrollToBottom } from "../../chat-scroll-context";

// Brand "default" purple ramp (tailwind.config.ts `default` scale). Index 0 is
// the single-series fill (bars); the rest give distinct on-brand pie/line hues.
const CHART_COLORS = [
  "#6368d1", // default-600
  "#9196e6", // default-400
  "#555abf", // default-700
  "#b8bbf0", // default-300
  "#4548a3", // default-800
  "#7277dc", // default-500
];

function Placeholder() {
  return (
    <div className="my-4 rounded-lg ring-1 ring-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400 text-center">
      Graphique en cours…
    </div>
  );
}

export function ChatChart({ spec }: { spec?: string }) {
  const scrollToBottom = useScrollToBottom();
  // On render this element adds height — jump to the bottom so we never land
  // mid-conversation when charts mount after the message text.
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const data = spec ? parsePartialJson(spec) : null;
  const type = data?.type as string | undefined;
  const rows = (data?.data as Array<Record<string, unknown>> | undefined) ?? [];
  const title = data?.title as string | undefined;

  if (!data || !type || rows.length === 0) return <Placeholder />;

  const Title = title ? (
    <div className="text-sm font-medium text-slate-700 mb-2">{title}</div>
  ) : null;

  if (type === "bar") {
    const horizontal = data.layout === "horizontal";
    // Axes MUST be direct children of <BarChart>. Recharts' findAllByType does
    // not descend into React fragments, so wrapping axes in a fragment makes it
    // drop the category binding and fall back to row indices for the labels.
    const categoryAxis = {
      type: "category" as const,
      dataKey: "label",
      tick: { fontSize: 12, fill: "#64748b" },
      interval: 0,
    };
    const valueAxis = {
      type: "number" as const,
      tick: { fontSize: 12, fill: "#64748b" },
    };
    return (
      <div className="my-4">
        {Title}
        <ResponsiveContainer
          width="100%"
          height={Math.max(220, horizontal ? rows.length * 40 + 60 : 280)}
        >
          <BarChart
            data={rows as any[]}
            layout={horizontal ? "vertical" : "horizontal"}
            margin={{ top: 4, right: 8, bottom: horizontal ? 4 : 64, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              {...(horizontal
                ? valueAxis
                : {
                    ...categoryAxis,
                    angle: -30,
                    textAnchor: "end" as const,
                    height: 70,
                  })}
            />
            <YAxis {...(horizontal ? { ...categoryAxis, width: 140 } : valueAxis)} />
            <Tooltip
              cursor={{ fill: "rgba(99, 104, 209, 0.08)" }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === "line") {
    const lines =
      (data.lines as Array<{ key: string; label?: string; color?: string }>) ??
      [];
    const xKey = (data.xKey as string) ?? "label";
    if (lines.length === 0) return <Placeholder />;
    return (
      <div className="my-4">
        {Title}
        <ResponsiveContainer width="100%" height={240}>
          <LineChart
            data={rows as any[]}
            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
            <Tooltip
              cursor={{ stroke: "#9196e6", strokeWidth: 1 }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            />
            {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {lines.map((l, i) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                stroke={l.color || CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                name={l.label || l.key}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === "pie") {
    const donut = Boolean(data.donut);
    return (
      <div className="my-4">
        {Title}
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={rows as any[]}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={donut ? 50 : 0}
              outerRadius={90}
              paddingAngle={2}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return <Placeholder />;
}
